import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Clapperboard, Film, Eye, EyeOff } from "lucide-react";

const CYCLE_WORDS = [
  "On Time.",
  "Without Fail.",
  "With Time to Spare.",
  "Every Shoot.",
  "Easier Than Ever.",
].filter(Boolean);

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const id = setInterval(() => setWordIdx(i => (i + 1) % CYCLE_WORDS.length), 5000);
    return () => clearInterval(id);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        if (!form.name.trim()) { setError("Name is required"); setLoading(false); return; }
        await register(form.email, form.password, form.name);
      }
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0F12] flex">
      {/* Left: Branding */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#14181F] border-r border-[#28303F] p-12">
        <div className="flex items-center gap-3">
          <Clapperboard className="text-amber-400" size={28} />
          <span className="text-2xl font-bold text-white font-[Outfit]">Get2Set</span>
        </div>
        <div>
          <div className="mb-8">
            <div className="w-16 h-1 bg-amber-400 mb-6 rounded" />
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight font-[Outfit]">
              <span className="whitespace-nowrap">Get2Set...</span>
              <br />
              <span
                key={wordIdx}
                data-testid="login-hero-cycle-word"
                className="text-amber-400 inline-block animate-[fadein_600ms_ease-out]"
              >
                {CYCLE_WORDS[wordIdx % CYCLE_WORDS.length] || CYCLE_WORDS[0]}
              </span>
            </h1>
            <p className="text-gray-400 mt-4 text-base leading-relaxed">
              Generate Hollywood-standard call sheets and distribute them to your entire crew via email and SMS — all in one place.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {["Projects", "CSV Import", "PDF Export"].map((f) => (
              <div key={f} className="bg-[#1A1F29] border border-[#28303F] rounded-xl p-4 text-center">
                <Film size={20} className="text-amber-400 mx-auto mb-2" />
                <p className="text-xs text-gray-400">{f}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-600">© 2026 Get2Set</p>
        <p className="text-xs text-gray-600 mt-1">
          <Link to="/privacy" data-testid="login-privacy-link" className="hover:text-gray-400 transition-colors">Privacy</Link>
          <span className="mx-2 text-gray-700">·</span>
          <Link to="/terms" data-testid="login-terms-link" className="hover:text-gray-400 transition-colors">Terms</Link>
        </p>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <Clapperboard className="text-amber-400" size={24} />
            <span className="text-xl font-bold text-white font-[Outfit]">Get2Set</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 font-[Outfit]">
            {mode === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-gray-400 text-sm mb-8">
            {mode === "login" ? "Sign in to your Get2Set account" : "Get started with Get2Set"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Full Name</label>
                <input
                  data-testid="register-name-input"
                  type="text" value={form.name} onChange={set("name")}
                  placeholder="Your full name"
                  className="w-full bg-[#1A1F29] border border-[#28303F] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Email</label>
              <input
                data-testid="login-email-input"
                type="email" value={form.email} onChange={set("email")}
                placeholder="you@example.com"
                className="w-full bg-[#1A1F29] border border-[#28303F] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"} value={form.password} onChange={set("password")}
                  placeholder="••••••••"
                  className="w-full bg-[#1A1F29] border border-[#28303F] rounded-lg px-4 py-3 pr-10 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit" disabled={loading}
              className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-3 px-6 rounded-lg transition-all duration-200 text-sm tracking-wide"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="register-toggle-button"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-sm text-gray-400 hover:text-amber-400 transition-colors"
            >
              {mode === "login" ? "Don't have an account? Create one" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
